<script setup lang="ts">
import { ref, computed, nextTick, watch, onMounted } from 'vue'
import { storeToRefs } from 'pinia'
import { useSmsStore } from '@/stores/sms'
import { toast } from 'vue-sonner'
import LoadingIcon from '@/components/LoadingIcon.vue'
import LeadAvatar from '@/components/LeadAvatar.vue'
import BaseButton from '@/components/BaseButton.vue'
import {
  isAiEventEntry,
  isAiSmsPaused,
  mergeAiEvents,
  visibleAiEvents,
  type TranscriptEntry,
} from '@/lib/aiPause'

import type { AiPauseEvent, Lead } from '@/types/types'

const emit = defineEmits<{
  back: []
  call: [lead: Lead]
}>()

const smsStore = useSmsStore()
const {
  currentConversation,
  messages,
  messagesLoading,
  hasMoreMessages,
  sendingMessage,
  aiEvents,
  resumingAi,
} = storeToRefs(smsStore)

const messageInput = ref('')
const messagesContainer = ref<HTMLElement | null>(null)
const messageInputEl = ref<HTMLTextAreaElement | null>(null)
const showMenu = ref(false)
const isDeleting = ref(false)
const selectedMessageIds = ref<Set<number>>(new Set())

const isLastMessage = (messageId: number) => {
  if (sortedMessages.value.length === 0) return false
  return sortedMessages.value[sortedMessages.value.length - 1].id === messageId
}

const shouldShowTime = (messageId: number) => {
  return isLastMessage(messageId) || selectedMessageIds.value.has(messageId)
}

const toggleMessageTime = (messageId: number) => {
  const newSet = new Set(selectedMessageIds.value)
  if (newSet.has(messageId)) {
    newSet.delete(messageId)
  } else {
    newSet.add(messageId)
  }
  selectedMessageIds.value = newSet
}

const handleCall = () => {
  if (!currentConversation.value?.lead) return
  // Passed through as-is. This used to split `label` into fname/lname when the
  // conversation payload carried no name — but `label` falls back to the
  // placeholder "Lead #245" for a lead that has none, and the dial endpoint
  // saves whatever names it is given, so calling a nameless lead permanently
  // renamed them to "Lead" "#245". ConversationResource now carries the real
  // fname/lname (callingly#1509), and where it has none the right answer is to
  // send none — makeOutboundCall drops empty names rather than posting them.
  emit('call', { ...currentConversation.value.lead })
}

const toggleMenu = () => {
  showMenu.value = !showMenu.value
}

const closeMenu = () => {
  showMenu.value = false
}

const handleDeleteConversation = async () => {
  if (!currentConversation.value || isDeleting.value) return

  if (!confirm('Are you sure you want to delete this conversation?')) {
    closeMenu()
    return
  }

  isDeleting.value = true
  const success = await smsStore.deleteConversation(currentConversation.value.id)
  isDeleting.value = false

  if (success) {
    emit('back')
  }
  closeMenu()
}

const sortedMessages = computed(() => {
  // Touch length to ensure reactivity on array changes
  void smsStore.messages.length
  return [...smsStore.messages].sort(
    (a, b) => new Date(a.sent_at).getTime() - new Date(b.sent_at).getTime(),
  )
})

/** Whether the rep has taken this conversation over from the AI. */
const aiPaused = computed(() => isAiSmsPaused(currentConversation.value))

// The transcript is a fixed height so the composer keeps its place in the app
// shell, which means the pause strip has to come out of the transcript rather
// than push the composer past the bottom. Derived from one set of numbers so
// the two can't drift apart.
const TRANSCRIPT_HEIGHT = 365
const STRIP_HEIGHT = 40
const STRIP_MARGIN_TOP = 8

const transcriptStyle = computed(() => ({
  height: `${TRANSCRIPT_HEIGHT - (aiPaused.value ? STRIP_HEIGHT + STRIP_MARGIN_TOP : 0)}px`,
}))

const stripStyle = { height: `${STRIP_HEIGHT}px`, marginTop: `${STRIP_MARGIN_TOP}px` }

// The takeover marker rides on the date divider's geometry: it is a marker in
// the transcript, the same kind of thing as "Today", not a message.
const DIVIDER_PILL = 'text-[11px] px-[10px] py-[3px] rounded-[10px]'

const groupedMessages = computed(() => {
  const merged = mergeAiEvents(
    sortedMessages.value,
    visibleAiEvents(currentConversation.value, aiEvents.value, sortedMessages.value),
  )
  const groups: { date: string; entries: TranscriptEntry[] }[] = []
  let currentDate = ''

  for (const entry of merged) {
    const entryDate = new Date(entry.sent_at).toLocaleDateString()
    if (entryDate !== currentDate) {
      currentDate = entryDate
      groups.push({ date: entryDate, entries: [] })
    }
    groups[groups.length - 1].entries.push(entry)
  }

  return groups
})

// Composed rather than interpolated into a template string with a placeholder:
// the app has no i18n, so these are plain sentences either way.
const aiEventLabel = (event: AiPauseEvent) => {
  if (event.action === 'ai_sms_resumed') {
    return event.user_name
      ? `${event.user_name} handed the conversation back to the AI`
      : 'Conversation handed back to the AI'
  }

  return event.user_name
    ? `${event.user_name} took over · AI paused`
    : 'Your team took over · AI paused'
}

const handleResumeAi = async () => {
  const result = await smsStore.resumeAiSms()
  if (!result.success && result.error) {
    toast.error(result.error)
  }
}

const formatDateHeader = (dateString: string) => {
  const date = new Date(dateString)
  const today = new Date()
  const yesterday = new Date(today)
  yesterday.setDate(yesterday.getDate() - 1)

  if (date.toDateString() === today.toDateString()) {
    return 'Today'
  } else if (date.toDateString() === yesterday.toDateString()) {
    return 'Yesterday'
  } else {
    return date.toLocaleDateString([], { weekday: 'long', month: 'long', day: 'numeric' })
  }
}

const formatTime = (dateString: string) => {
  return new Date(dateString).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
}

const formatFullDateTime = (dateString: string) => {
  return new Date(dateString).toLocaleString([], {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

const scrollToBottom = () => {
  nextTick(() => {
    if (messagesContainer.value) {
      messagesContainer.value.scrollTop = messagesContainer.value.scrollHeight
    }
  })
}

const handleScroll = () => {
  if (messagesContainer.value && messagesContainer.value.scrollTop === 0) {
    if (hasMoreMessages.value && !messagesLoading.value) {
      const prevHeight = messagesContainer.value.scrollHeight
      smsStore.loadMoreMessages().then(() => {
        nextTick(() => {
          if (messagesContainer.value) {
            messagesContainer.value.scrollTop = messagesContainer.value.scrollHeight - prevHeight
          }
        })
      })
    }
  }
}

const handleSend = async () => {
  if (!messageInput.value.trim() || sendingMessage.value) return

  const message = messageInput.value
  messageInput.value = ''

  const result = await smsStore.sendMessage(message)
  if (result.success) {
    scrollToBottom()
  } else {
    messageInput.value = message
    if (result.error) {
      toast.error(result.error)
    }
  }

  messageInputEl.value?.focus()
}

const handleKeydown = (event: KeyboardEvent) => {
  if (event.key === 'Enter' && !event.shiftKey) {
    event.preventDefault()
    handleSend()
  }
}

watch(
  () => smsStore.messages.length,
  (newLength, oldLength) => {
    if (newLength > oldLength) {
      scrollToBottom()
    }
  },
)

onMounted(() => {
  scrollToBottom()
  messageInputEl.value?.focus()
})
</script>

<template>
  <div class="bg-white -mt-[20px]">
    <div class="flex items-center px-[16px] py-[8px] border-b border-[#eee] bg-white">
      <button
        data-testid="back-button"
        class="bg-transparent border-none p-[4px] cursor-pointer flex items-center justify-center"
        @click="emit('back')"
      >
        <i class="material-icons text-[24px] text-[#073b4c]">arrow_back</i>
      </button>
      <div class="flex items-center ml-[8px] flex-1">
        <LeadAvatar :name="currentConversation?.lead?.label" size="md" class="mr-[10px]" />
        <div class="flex flex-col">
          <div class="font-semibold text-[#073b4c] text-[14px]">
            {{ currentConversation?.lead?.label || 'Unknown' }}
          </div>
          <div class="flex flex-col text-[11px] text-[#808080]">
            <span>To: {{ currentConversation?.lead?.phone_number_formatted }}</span>
            <span v-if="currentConversation?.number">
              From:
              {{
                currentConversation?.number?.name
                  ? `${currentConversation.number.name} - ${currentConversation.number.phone_number_formatted}`
                  : currentConversation?.number?.phone_number_formatted
              }}
            </span>
          </div>
        </div>
      </div>
      <div class="flex items-center relative">
        <button
          v-if="currentConversation?.lead"
          class="bg-transparent border-none p-[4px] cursor-pointer flex items-center justify-center rounded-full hover:bg-[#f0f0f0]"
          data-testid="menu-button"
          @click="handleCall"
        >
          <i class="material-icons text-[24px] text-[#073b4c]">phone</i>
        </button>
        <button
          class="bg-transparent border-none p-[4px] cursor-pointer flex items-center justify-center rounded-full hover:bg-[#f0f0f0]"
          data-testid="menu-button"
          @click="toggleMenu"
        >
          <i class="material-icons text-[24px] text-[#073b4c]">more_vert</i>
        </button>
        <div
          v-if="showMenu"
          data-testid="menu-dropdown"
          class="absolute top-full right-0 bg-white rounded-[8px] shadow-[0_2px_10px_rgba(0,0,0,0.15)] min-w-[180px] z-[100] overflow-hidden"
        >
          <div
            data-testid="menu-item"
            class="flex items-center px-[16px] py-[12px] cursor-pointer transition-[background-color] duration-200 hover:bg-[#f5f5f5] text-[#ef4444]"
            @click="handleDeleteConversation"
          >
            <i class="material-icons text-[20px]! mr-[12px]">delete</i>
            <span>Delete</span>
          </div>
        </div>
        <div v-if="showMenu" class="fixed inset-0 z-[99]" @click="closeMenu"></div>
      </div>
    </div>

    <div
      ref="messagesContainer"
      data-testid="messages-container"
      class="overflow-y-auto p-[12px] bg-[#f5f5f5]"
      :style="transcriptStyle"
      @scroll="handleScroll"
    >
      <div
        v-if="messagesLoading && messages.length === 0"
        class="flex items-center justify-center h-full text-[#808080]"
      >
        <LoadingIcon />
      </div>

      <div
        v-else-if="messages.length === 0"
        class="flex items-center justify-center h-full text-[#808080]"
      >
        <p>No messages yet. Start the conversation!</p>
      </div>

      <template v-else>
        <div v-if="hasMoreMessages" class="flex justify-center mb-[12px]">
          <LoadingIcon v-if="messagesLoading" />
          <BaseButton v-else size="xs" @click="smsStore.loadMoreMessages()">
            Load older messages
          </BaseButton>
        </div>

        <div v-for="group in groupedMessages" :key="group.date" class="mb-[12px]">
          <div data-testid="date-divider" class="text-center my-[12px]">
            <span :class="[DIVIDER_PILL, 'bg-[#e0e0e0] text-[#666]']">{{
              formatDateHeader(group.date)
            }}</span>
          </div>

          <template
            v-for="entry in group.entries"
            :key="isAiEventEntry(entry) ? `ai-event-${entry.id}` : entry.id"
          >
            <div
              v-if="isAiEventEntry(entry)"
              data-testid="ai-event-divider"
              class="text-center my-[12px]"
            >
              <span
                :class="[
                  DIVIDER_PILL,
                  'inline-flex items-center gap-[5px] bg-[#eceff1] text-[#566a75] font-semibold leading-[18px]',
                ]"
              >
                <i class="material-icons text-[13px]! text-[#7b8794] leading-none">{{
                  entry.action === 'ai_sms_paused' ? 'pause_circle_outline' : 'play_circle_outline'
                }}</i>
                {{ aiEventLabel(entry) }}
                <span class="font-normal opacity-75">· {{ formatTime(entry.sent_at) }}</span>
              </span>
            </div>

            <div
              v-else
              data-testid="message-bubble-wrapper"
              class="flex flex-col mb-[6px]"
              :class="entry.direction === 'outbound' ? 'items-end' : 'items-start'"
              @click="toggleMessageTime(entry.id)"
            >
              <div
                class="max-w-[80%] px-[12px] py-[8px] shadow-[0_1px_2px_rgba(0,0,0,0.1)]"
                :class="
                  entry.is_error
                    ? 'rounded-[16px] bg-[#ffebee] border border-[#ef5350] text-[#ef5350]'
                    : entry.actor === 'ai'
                      ? 'rounded-[18px] bg-[linear-gradient(315deg,#32a8d9,#2dd4bf)] text-white'
                      : entry.actor === 'system'
                        ? 'rounded-[18px] bg-[#8b95a2] text-white'
                        : entry.direction === 'outbound'
                          ? 'rounded-[16px] bg-[#32a8d9] text-white'
                          : 'rounded-[16px] bg-white'
                "
              >
                <p class="m-0 break-words whitespace-pre-wrap text-[14px]">{{ entry.body }}</p>
                <div v-if="entry.media_url" class="mt-[6px]">
                  <img class="max-w-full rounded-[8px]" :src="entry.media_url" alt="Media" />
                </div>
              </div>
              <div
                v-if="shouldShowTime(entry.id) || entry.is_error"
                class="flex flex-row items-center gap-[2px] mt-[4px] text-[11px] text-[#808080]"
              >
                <div
                  v-if="entry.is_error"
                  class="flex items-center text-[#ef5350] text-[11px] leading-none"
                >
                  <i class="material-icons text-[12px]! mr-[4px] leading-none">error_outline</i>
                  <span>SMS failed to send</span>
                </div>
                <span
                  data-testid="message-time"
                  class="inline-flex items-center gap-[2px] text-[12px] leading-none"
                  :class="[
                    entry.is_error ? 'text-[#d32f2f]' : 'text-[#808080]',
                    entry.direction !== 'outbound' ? 'flex-row-reverse' : '',
                  ]"
                  ><span v-if="entry.actor"
                    ><span v-if="entry.actor === 'ai'" title="AI Generated Message"
                      ><svg
                        class="inline-block w-[12px] h-[12px] text-[#2dd4bf]"
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        fill="currentColor"
                      >
                        <path
                          fill-rule="evenodd"
                          d="M9 4.5a.75.75 0 0 1 .721.544l.813 2.846a3.75 3.75 0 0 0 2.576 2.576l2.846.813a.75.75 0 0 1 0 1.442l-2.846.813a3.75 3.75 0 0 0-2.576 2.576l-.813 2.846a.75.75 0 0 1-1.442 0l-.813-2.846a3.75 3.75 0 0 0-2.576-2.576l-2.846-.813a.75.75 0 0 1 0-1.442l2.846-.813A3.75 3.75 0 0 0 7.466 7.89l.813-2.846A.75.75 0 0 1 9 4.5ZM18 1.5a.75.75 0 0 1 .728.568l.258 1.036c.236.94.97 1.674 1.91 1.91l1.036.258a.75.75 0 0 1 0 1.456l-1.036.258c-.94.236-1.674.97-1.91 1.91l-.258 1.036a.75.75 0 0 1-1.456 0l-.258-1.036a2.625 2.625 0 0 0-1.91-1.91l-1.036-.258a.75.75 0 0 1 0-1.456l1.036-.258a2.625 2.625 0 0 0 1.91-1.91l.258-1.036A.75.75 0 0 1 18 1.5ZM16.5 15a.75.75 0 0 1 .712.513l.394 1.183c.15.447.5.799.948.948l1.183.395a.75.75 0 0 1 0 1.422l-1.183.395c-.447.15-.799.5-.948.948l-.395 1.183a.75.75 0 0 1-1.422 0l-.395-1.183a1.5 1.5 0 0 0-.948-.948l-1.183-.395a.75.75 0 0 1 0-1.422l1.183-.395c.447-.15.799-.5.948-.948l.395-1.183A.75.75 0 0 1 16.5 15Z"
                          clip-rule="evenodd"
                        /></svg></span
                    ><span v-else-if="entry.actor === 'lead'" title="Lead"
                      ><svg
                        class="inline-block w-[12px] h-[12px] text-[#999]"
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          d="M10 8a3 3 0 1 0 0-6 3 3 0 0 0 0 6ZM3.465 14.493a1.23 1.23 0 0 0 .41 1.412A9.957 9.957 0 0 0 10 18c2.31 0 4.438-.784 6.131-2.1.43-.333.604-.903.408-1.41a7.002 7.002 0 0 0-13.074.003Z"
                        /></svg></span
                    ><span v-else-if="entry.actor === 'user'" title="User"
                      ><svg
                        class="inline-block w-[12px] h-[12px] text-[#32a8d9] ml-[1.5px]"
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fill-rule="evenodd"
                          d="M18 10a8 8 0 1 1-16 0 8 8 0 0 1 16 0Zm-5.5-2.5a2.5 2.5 0 1 1-5 0 2.5 2.5 0 0 1 5 0ZM10 12a4.002 4.002 0 0 0-3.874 3.022.75.75 0 0 0 .715.978h6.318a.75.75 0 0 0 .715-.978A4.002 4.002 0 0 0 10 12Z"
                          clip-rule="evenodd"
                        /></svg></span
                    ><span v-else-if="entry.actor === 'system'" title="System"
                      ><svg
                        class="inline-block w-[12px] h-[12px] text-[#999]"
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fill-rule="evenodd"
                          d="M7.84 1.804A1 1 0 0 1 8.82 1h2.36a1 1 0 0 1 .98.804l.331 1.652a6.993 6.993 0 0 1 1.929 1.115l1.598-.54a1 1 0 0 1 1.186.447l1.18 2.044a1 1 0 0 1-.205 1.251l-1.267 1.113a7.047 7.047 0 0 1 0 2.228l1.267 1.113a1 1 0 0 1 .206 1.25l-1.18 2.045a1 1 0 0 1-1.187.447l-1.598-.54a6.993 6.993 0 0 1-1.929 1.115l-.33 1.652a1 1 0 0 1-.98.804H8.82a1 1 0 0 1-.98-.804l-.331-1.652a6.993 6.993 0 0 1-1.929-1.115l-1.598.54a1 1 0 0 1-1.186-.447l-1.18-2.044a1 1 0 0 1 .205-1.251l1.267-1.114a7.05 7.05 0 0 1 0-2.227L1.821 7.773a1 1 0 0 1-.206-1.25l1.18-2.045a1 1 0 0 1 1.187-.447l1.598.54A6.992 6.992 0 0 1 7.51 3.456l.33-1.652ZM10 13a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z"
                          clip-rule="evenodd"
                        /></svg></span></span
                  ><span :title="formatFullDateTime(entry.sent_at)">{{
                    formatTime(entry.sent_at)
                  }}</span></span
                >
              </div>
            </div>
          </template>
        </div>
      </template>
    </div>

    <!-- Directly above the composer, not at the top of the thread: the top
         scrolls away, and the composer is where the rep's attention is. -->
    <div
      v-if="aiPaused"
      data-testid="ai-pause-strip"
      class="flex items-center gap-[8px] mx-[12px] px-[12px] bg-[#eceff1] border border-[#dfe4e7] rounded-[4px] text-[12px] leading-[16px] text-[#40535d]"
      :style="stripStyle"
    >
      <i class="material-icons text-[15px]! text-[#7b8794] leading-none">pause_circle_outline</i>
      <span class="flex-1 min-w-0">
        <strong>You're handling this conversation.</strong> AI replies are paused.
      </span>
      <!-- Deliberately not BaseButton: its xs size is 22px tall, under this
           app's own touch-target floor (send is 36px, the menu buttons 32px),
           and overriding its padding without a class-merge leaves the width at
           the mercy of stylesheet order — which wraps this strip's copy to a
           third line and clips it. -->
      <button
        data-testid="ai-pause-resume"
        class="shrink-0 h-[30px] bg-white border border-[#c3ccd1] rounded-[4px] px-[10px] text-[11px] font-semibold text-[#40535d] cursor-pointer whitespace-nowrap enabled:hover:bg-[#dfe4e7] disabled:opacity-50 disabled:cursor-not-allowed"
        :disabled="resumingAi"
        @click="handleResumeAi"
      >
        Hand back to AI
      </button>
    </div>

    <div class="flex items-end px-[12px] py-[10px] border-t border-[#eee] bg-white">
      <textarea
        ref="messageInputEl"
        v-model="messageInput"
        data-testid="message-input"
        class="flex-1 border border-[#ddd] rounded-[18px] px-[14px] py-[8px] text-[14px] resize-none outline-none max-h-[80px] focus:border-[#32a8d9]"
        placeholder="Type a message..."
        rows="1"
        @keydown="handleKeydown"
      />
      <button
        data-testid="send-button"
        class="bg-[#32a8d9] border-none rounded-full w-[36px] h-[36px] ml-[8px] cursor-pointer flex items-center justify-center shrink-0 transition-[background-color] duration-200 enabled:hover:bg-[#2899c7] disabled:bg-[#ccc] disabled:cursor-not-allowed"
        :disabled="!messageInput.trim() || sendingMessage"
        @mousedown.prevent
        @click="handleSend"
      >
        <LoadingIcon v-if="sendingMessage" />
        <i v-else class="material-icons text-white text-[18px]!">send</i>
      </button>
    </div>
  </div>
</template>
